<template>
    <BasePage>
        <div class="container">
            <section class="home-intro mt-3 mt-md-5" aria-labelledby="home-heading" data-testid="home-intro">
                <div class="row align-items-end g-3">
                    <div class="col-12 col-md">
                        <h1 id="home-heading" class="h2 mb-0">Christmas orders</h1>
                    </div>
                    <div class="col-12 col-md-4">
                        <label class="form-label visually-hidden" for="home-search">Search all products</label>
                        <input id="home-search" type="search" class="form-control" v-model="searchText"
                               placeholder="Search all products" autocomplete="off" data-testid="home-search"
                               @input="loadEveryProduct()">
                    </div>
                </div>
                <div v-show="!searching" class="mt-3">
                    <p class="mb-2">Order for Christmas online from Larkhall Butchers: meat, wine, cheese and more.</p>
                    <ol class="home-steps list-unstyled mb-0">
                        <li><span class="home-step-number" aria-hidden="true">1</span><span><strong>Choose what you'd like.</strong> Browse the categories or search, and add to your basket.</span></li>
                        <li><span class="home-step-number" aria-hidden="true">2</span><span><strong>Pick collection or delivery.</strong> Choose where and when at checkout.</span></li>
                        <li><span class="home-step-number" aria-hidden="true">3</span><span><strong data-testid="home-deposit-step">{{ depositStep }}</strong> The rest is paid later. See the <router-link to="/FAQ">FAQ</router-link> for how payment works.</span></li>
                    </ol>
                    <p class="text-secondary mt-2 mb-0">Questions? Email us at <a href="mailto:info@larkhallbutchers.co.uk">info@larkhallbutchers.co.uk</a>.</p>
                </div>
            </section>
            <template v-if="searching">
                <div v-if="searchError" class="mt-4" role="alert">
                    We couldn't search the shop just now. Please check your connection.
                    <button type="button" class="btn btn-link p-0 align-baseline" @click="loadEveryProduct()">Try again</button>
                </div>
                <p v-else-if="!everyProduct" class="mt-4 text-secondary" role="status">Searching…</p>
                <template v-else>
                    <p class="mt-4 mb-0" role="status" data-testid="home-search-status">
                        <template v-if="searchResults.matches.length === 0 && searchResults.alsoMentioned.length === 0">No products match "{{ searchText.trim() }}".</template>
                        <template v-else-if="searchResults.matches.length === 0">No product names match "{{ searchText.trim() }}", but some descriptions mention it.</template>
                        <template v-else>{{ searchResults.matches.length }} {{ searchResults.matches.length === 1 ? 'product matches' : 'products match' }} "{{ searchText.trim() }}".</template>
                        <button type="button" class="btn btn-link p-0 align-baseline ms-1" @click="clearSearch()">Clear the search</button>
                    </p>
                    <ProductTiles v-if="searchResults.matches.length > 0" :isHome="true" :products="searchResults.matches"></ProductTiles>
                    <template v-if="searchResults.alsoMentioned.length > 0">
                        <h2 class="h3 mt-5 mb-0" data-testid="home-search-mentioned">Also mentioned in descriptions</h2>
                        <ProductTiles :isHome="true" :products="searchResults.alsoMentioned"></ProductTiles>
                    </template>
                </template>
            </template>
            <div v-else-if="loadError" class="card mt-5 mb-6 py-6 px-3 text-center" role="alert">
                <h2>We couldn't load the shop</h2>
                <p>Please check your connection and try again.</p>
                <div><button type="button" class="btn btn-primary" @click="getProducts()">Try again</button></div>
            </div>
            <ProductTiles v-else :isHome="true" :products="products"></ProductTiles>
        </div>
    </BasePage>
</template>

<script>
import ProductTiles from "@/components/ProductTiles.vue";
import api from '@/services/api'
import BasePage from '@/components/BasePage.vue';
import { indexProducts, searchProducts } from '@/services/productSearch';
import { depositPolicy, formatDepositAmount, isFixedDeposit, isPercentDeposit, loadDepositPolicy } from '@/services/depositPolicy';

export default {
    name: 'HomePage',
    components: {
        ProductTiles,
        BasePage,
    },
    data() {
        return {
            products: [],
            loadError: false,
            searchText: '',
            // The whole shop, loaded the first time someone types in the search box. null = not loaded.
            everyProduct: null,
            everyProductLoading: false,
            searchError: false
        }
    },
    mounted() {
        this.getProducts();
        loadDepositPolicy();
    },
    computed: {
        // WP-37: the deposit step follows GET /api/checkout/deposit-policy. No amount while it loads;
        // if it fails, the per-item wording (what the API does when no fixed deposit is set).
        depositStep() {
            if (isFixedDeposit()) return `Pay a ${formatDepositAmount(depositPolicy.orderDeposit)} deposit for your whole order.`;
            if (isPercentDeposit()) return `Pay a deposit of ${depositPolicy.orderDepositPercent}% of your estimated order total, rounded down to the nearest pound.`;
            if (depositPolicy.status === 'loading') return 'Pay a deposit.';
            return `Pay a ${formatDepositAmount(depositPolicy.baseDeposit)} deposit.`;
        },
        searching() {
            return this.searchText.trim().length > 0;
        },
        // Forgiving search (services/productSearch.js): names, categories, cuts and codes, with
        // allowance for typos, plurals and partial words; description-only matches listed apart.
        searchResults() {
            if (!this.everyProduct) return { matches: [], alsoMentioned: [] };
            return searchProducts(this.everyProduct, this.searchText);
        }
    },
    methods: {
        // FE-14: a failed request shows a message and a retry instead of a blank page.
        async getProducts() {
            this.loadError = false;
            try {
                const productsRes = await api.get('/api/products/home');
                if (!Array.isArray(productsRes.data)) throw new Error('Unexpected reply');
                this.products = productsRes.data;
            } catch (e) {
                this.loadError = true;
            }
        },
        // The same three routes the category pages read. /api/products carries the category id in
        // `group`; the tiles on this page link by category slug (as /api/products/home returns it),
        // so the id is swapped for the slug from /api/groups. A product whose category is unknown
        // could not be linked to and is left out.
        async loadEveryProduct() {
            if (this.everyProduct || this.everyProductLoading || !this.searching) return;
            this.everyProductLoading = true;
            this.searchError = false;
            try {
                const [groupsRes, typesRes, productsRes] = await Promise.all([
                    api.get('/api/groups'),
                    api.get('/api/types'),
                    api.get('/api/products'),
                ]);
                if (!Array.isArray(groupsRes.data) || !Array.isArray(typesRes.data) || !Array.isArray(productsRes.data)) {
                    throw new Error('Unexpected reply');
                }
                const groups = new Map(groupsRes.data.map(g => [g.groupId, g]));
                const typeNames = new Map(typesRes.data.map(t => [t.typeId, t.typeName]));
                const linkable = productsRes.data
                    .filter(p => groups.get(p.group) && groups.get(p.group).groupSlug)
                    .map(p => {
                        const group = groups.get(p.group);
                        return { ...p, group: group.groupSlug, groupName: group.groupName, typeName: typeNames.get(p.type) };
                    });
                this.everyProduct = indexProducts(linkable, p => [p.groupName, p.typeName].filter(Boolean));
            } catch (e) {
                this.searchError = true;
            } finally {
                this.everyProductLoading = false;
            }
        },
        clearSearch() {
            this.searchText = '';
            const box = document.getElementById('home-search');
            if (box) box.focus();
        }
    }
};
</script>

<style scoped>
/* One quiet band: hairlines above and below, plain numerals in the brand gold, no cards. */
.home-steps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem 2rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--tblr-border-color);
    border-bottom: 1px solid var(--tblr-border-color);
}
.home-steps li {
    display: flex;
    align-items: baseline;
    gap: 0.625rem;
    color: var(--tblr-secondary);
}
.home-steps strong {
    color: var(--tblr-body-color);
}
.home-step-number {
    flex: none;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1;
    color: var(--tblr-primary);
}
@media (max-width: 767.98px) {
    .home-steps {
        grid-template-columns: 1fr;
        gap: 0.375rem;
        padding: 0.5rem 0;
        font-size: 0.875rem;
    }
    .home-step-number {
        font-size: 1rem;
        min-width: 0.75rem;
    }
}
</style>
